import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { esgService } from "../services";
import { normalizeNumbers, extractDataPayload } from "../utils/api-normalizers";

/**
 * Carbon Targets Store
 * Manages Scope 1 and Scope 2 carbon reduction targets
 */
class CarbonTargetsStore {
  carbonTargets = new Map();
  loading = false;
  saving = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
  }

  /**
   * Generate cache key for carbon targets by year
   */
  getCarbonYearKey = (year) => {
    return `carbon_${year}`;
  };

  /**
   * Get carbon targets for a specific year
   * Initializes empty structure if not present
   */
  getCarbonTargets = (year) => {
    const key = this.getCarbonYearKey(year);
    if (!this.carbonTargets.has(key)) {
      this.carbonTargets.set(key, { scope1: {}, scope2: {} });
    }
    return this.carbonTargets.get(key);
  };

  /**
   * Set a specific carbon value
   * @param {string} scopeId - 'scope1' or 'scope2'
   * @param {string} fieldKey - Field name
   * @param {any} value - Value to set
   * @param {number} year - Target year
   */
  setCarbonValue = (scopeId, fieldKey, value, year) => {
    const key = this.getCarbonYearKey(year);
    const current = this.getCarbonTargets(year);
    const updated = {
      ...current,
      [scopeId]: {
        ...(current[scopeId] || {}),
        [fieldKey]: value,
      },
    };
    this.carbonTargets.set(key, updated);
  };

  /**
   * Parse and set carbon targets from API response
   * @param {number} year - Target year
   * @param {object} payload - API response payload
   */
  setCarbonTargetsFromApi = (year, payload = {}) => {
    const key = this.getCarbonYearKey(year);
    const normalized = { scope1: {}, scope2: {} };

    // Direct scope1/scope2 in payload
    if (payload.scope1 || payload.scope2) {
      normalized.scope1 = payload.scope1 || {};
      normalized.scope2 = payload.scope2 || {};
    }

    // Check for nested array format
    const candidates = payload.targets || payload.data || payload.items || [];
    if (Array.isArray(candidates)) {
      const match = candidates.find(
        (item) => parseInt(item.year, 10) === parseInt(year, 10),
      );
      if (match && (match.scope1 || match.scope2)) {
        normalized.scope1 = match.scope1 || {};
        normalized.scope2 = match.scope2 || {};
      }
    }

    this.carbonTargets.set(key, normalized);
  };

  /**
   * Load carbon targets from API
   * @param {number} year - Target year
   * @returns {Promise<boolean>} Success status
   */
  loadCarbonTargets = async (year) => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const data = await esgService.getCarbonTargets({
        year,
        throwOnError: false,
      });

      if (!data) {
        throw new Error("Failed to load carbon targets.");
      }

      const payload = extractDataPayload(data);
      runInAction(() => {
        this.setCarbonTargetsFromApi(year, payload);
      });

      return true;
    } catch (error) {
      showToast.error(error?.message || "Failed to load carbon targets.");
      return false;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  /**
   * Save carbon targets to API
   * @param {number} year - Target year
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  saveCarbonTargets = async (year) => {
    runInAction(() => {
      this.saving = true;
    });

    try {
      const carbonTargets = this.getCarbonTargets(year);
      const payload = {
        year,
        scope1: normalizeNumbers(carbonTargets?.scope1),
        scope2: normalizeNumbers(carbonTargets?.scope2),
      };

      const data = await esgService.saveCarbonTargets(payload);

      const targets = data?.data?.targets || data?.targets || [];
      runInAction(() => {
        targets.forEach((item) => {
          if (!item?.year) return;
          this.setCarbonTargetsFromApi(item.year, item);
        });
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error?.message || "Failed to save carbon targets.",
      };
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  };

  /**
   * Clear carbon targets for a specific year
   * @param {number} year - Target year
   */
  clearCarbonTargets = (year) => {
    const key = this.getCarbonYearKey(year);
    this.carbonTargets.delete(key);
  };
}

export const carbonTargetsStore = new CarbonTargetsStore();
